/** @format */

import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn, Button } from '@clarix/ui';

interface InformativeBarProps {
  /** Primary page title (string or custom node). Omit when using `leading`. */
  title?: ReactNode;
  /** Secondary line / contextual info shown next to the title. */
  info?: ReactNode;
  /** Leading visual: an icon node or an avatar element, rendered before the title. */
  icon?: ReactNode;
  /** A count / status pill rendered immediately after the title. */
  badge?: ReactNode;
  /** Show a back button on the left; called on click. */
  onBack?: () => void;
  /** Replace the whole title block with custom left content (breadcrumb, etc.). */
  leading?: ReactNode;
  /** Right-aligned content: action buttons, filters, avatars, etc. */
  children?: ReactNode;
  className?: string;
}

/**
 * The page identity / header row — "who/what am I looking at".
 *
 * Holds the title, contextual info, icon/avatar, count badge and an optional
 * back button on the left; arbitrary actions on the right. Fixed `h-12`,
 * `shrink-0`, bottom border — identical height + padding on every page.
 */
export function InformativeBar({
  title,
  info,
  icon,
  badge,
  onBack,
  leading,
  children,
  className,
}: InformativeBarProps) {
  return (
    <div
      className={cn(
        'border-border bg-surface flex h-11 shrink-0 items-center gap-3 border-b px-5',
        className,
      )}
    >
      {onBack && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1 shrink-0"
          onClick={onBack}
          aria-label="Go back"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      )}

      {leading ?? (
        <div className="flex min-w-0 items-center gap-2">
          {icon && <span className="text-muted-foreground flex shrink-0 items-center">{icon}</span>}
          {title && (
            <h1 className="text-foreground truncate text-xl font-semibold tracking-tight">
              {title}
            </h1>
          )}
          {badge && <span className="shrink-0">{badge}</span>}
          {info && (
            <span className="text-muted-foreground truncate text-lg font-medium">{info}</span>
          )}
        </div>
      )}

      {children && <div className="ml-auto flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  );
}
